import React, { useEffect, useState } from 'react';
import StickyMenu from '../../components/common/StickyMenu';
import Footer from '../shared/Footer';
import Navbar from '../shared/Navbar';
import SettingsPanel from '../shared/SettingsPanel';
import Sidebar from '../shared/Sidebar';
import instructorService from '../../services/instructorService';
import { useTranslation } from 'react-i18next';
import cookies from 'js-cookie';
import { URI } from '../../services/service';
import UserService from '../../services/UserService';
import './AdminScreenshotGallery.css';

const languages = [
    {
        code: 'en',
        name: 'English',
        country_code: 'gb',
    },
];

const AdminScreenshotGallery = () => {
    const currentLanguageCode = cookies.get('i18next') || 'en';
    const currentLanguage = languages.find((l) => l.code === currentLanguageCode);
    const { t } = useTranslation();

    useEffect(() => {
        document.body.dir = currentLanguage.dir || 'ltr';
        document.title = t('app_title');
    }, [currentLanguage, t]);

    useEffect(() => {
        UserService.generateToken();
    }, []);

    const [screenshots, setScreenshots] = useState([]);
    const [filteredScreenshots, setFilteredScreenshots] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [message, setMessage] = useState(null);

    const itemsPerPage = 6;
    const BASE_URL = URI.SERVER?._DOMAIN_URL;

    // Fetch Screenshots
    const fetchScreenshots = async () => {
        try {
            const res = await instructorService.getAllScreenShotImage();
            if (!res.data) throw new Error("Failed to fetch screenshots");
            //console.log(res.data);
            // const data = await res.json();
            setScreenshots(res.data);
            setFilteredScreenshots(res.data);
        } catch (err) {
            console.error("Error fetching screenshots:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchScreenshots();
    }, []);

    // Filter logic
    useEffect(() => {
        let filtered = screenshots;

        if (searchTerm.trim()) {
            filtered = filtered.filter(
                (item) =>
                    item.contentDesc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    item.uploadedBy?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (dateFilter.from || dateFilter.to) {
            filtered = filtered.filter((item) => {
                const uploadedDate = new Date(item.uploadedAt);
                const from = dateFilter.from ? new Date(dateFilter.from) : null;
                const to = dateFilter.to ? new Date(dateFilter.to) : null;
                return (!from || uploadedDate >= from) && (!to || uploadedDate <= to);
            });
        }

        setFilteredScreenshots(filtered);
        setCurrentPage(1);
    }, [searchTerm, dateFilter, screenshots]);

    // Download image
    const handleDownload = (fileUrl, fileName) => {
        const link = document.createElement("a");
        link.href = `${BASE_URL}${fileUrl}`;
        link.download = fileName;
        link.click();
    };

    // Delete image
    const handleDelete = async (contentId) => {

        try {
            const res = await instructorService.deleteConentbyAdmin(contentId);
            if (!res.data) throw new Error("Failed to delete image");

            if(res.data == "Content deleted successfully!"){
                 setMessage({ type: "success", text: "Screenshot deleted successfully!" });
                setDeleteConfirm(null);
                fetchScreenshots();
            }
            else{
                setMessage({ type: "error", text: res.data });
            }
           
        } catch (err) {
             console.error("Delete failed:", err);
            setMessage({ type: "error", text: "Failed to delete screenshot." });
        } 

        setTimeout(() => setMessage(null), 3000);
    };

    // Pagination
    const totalPages = Math.ceil(filteredScreenshots.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const visibleItems = filteredScreenshots.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="container-scroller">
                <Navbar />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <div className="main-panel">
                        <div className="content-wrapper">
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>Loading screenshots...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <Sidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="page-header">
                            <h3 className="page-title">User Screenshots</h3>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <a href="#!" onClick={event => event.preventDefault()}>
                                            User Submission
                                        </a>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Screenshots
                                    </li>
                                </ol>
                            </nav>
                        </div>

                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    <div className="screenshot-gallery-container">
                                        <h2 className="gallery-title">
                                            <span className="icon">📸</span> Captured Screenshots
                                        </h2>

                                        {/* Message Toast */}
                                        {message && (
                                            <div className={`toast-message ${message.type}`}>
                                                {message.text}
                                            </div>
                                        )}

                                        {/* Filter Controls */}
                                        <div className="filter-controls">
                                            <input
                                                type="text"
                                                placeholder="🔍 Search by description or user"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="search-input"
                                            />

                                            <div className="date-filter-group">
                                                <div className="date-input-wrapper">
                                                    <label>From:</label>
                                                    <input
                                                        type="date"
                                                        value={dateFilter.from}
                                                        onChange={(e) =>
                                                            setDateFilter((prev) => ({ ...prev, from: e.target.value }))
                                                        }
                                                        className="date-input"
                                                    />
                                                </div>

                                                <div className="date-input-wrapper">
                                                    <label>To:</label>
                                                    <input
                                                        type="date"
                                                        value={dateFilter.to}
                                                        onChange={(e) =>
                                                            setDateFilter((prev) => ({ ...prev, to: e.target.value }))
                                                        }
                                                        className="date-input"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => {
                                                    setSearchTerm("");
                                                    setDateFilter({ from: "", to: "" });
                                                }}
                                                className="reset-btn"
                                            >
                                                Reset
                                            </button>
                                        </div>

                                        {/* Screenshot Grid */}
                                        {filteredScreenshots.length === 0 ? (
                                            <p className="no-data-message">No screenshots found.</p>
                                        ) : (
                                            <div className="screenshot-grid">
                                                {visibleItems.map((item) => (
                                                    <div key={item.contentId} className="screenshot-card">
                                                        <div className="image-wrapper">
                                                            <img
                                                                src={`${BASE_URL}${item.contentUrl}`}
                                                                alt={item.contentName}
                                                                className="screenshot-image"
                                                                onClick={() => setSelectedImage(item)}
                                                            />
                                                        </div>
                                                        <div className="card-content">
                                                            <p className="description">
                                                                {item.contentDesc || "No description"}
                                                            </p>
                                                            <p className="date-info">
                                                                📅{" "}
                                                                {new Date(item.uploadedAt).toLocaleString("en-IN", {
                                                                    hour12: true,
                                                                })}
                                                            </p>
                                                            <p className="user-info">
                                                                👤 Uploaded By: {item.uploadedBy}
                                                            </p>
                                                            <div className="action-buttons">
                                                                <button
                                                                    onClick={() =>
                                                                        handleDownload(item.contentUrl, item.contentName)
                                                                    }
                                                                    className="btn btn-download"
                                                                >
                                                                    ⬇️ Download
                                                                </button>
                                                                <button
                                                                    onClick={() => setSelectedImage(item)}
                                                                    className="btn btn-view"
                                                                >
                                                                    🔍 View
                                                                </button>
                                                                <button
                                                                    onClick={() => setDeleteConfirm(item)}
                                                                    className="btn btn-delete"
                                                                >
                                                                    🗑 Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="pagination">
                                                <button
                                                    disabled={currentPage === 1}
                                                    onClick={() => setCurrentPage((p) => p - 1)}
                                                    className="pagination-btn"
                                                >
                                                    ⬅ Prev
                                                </button>
                                                <span className="page-info">
                                                    Page {currentPage} of {totalPages}
                                                </span>
                                                <button
                                                    disabled={currentPage === totalPages}
                                                    onClick={() => setCurrentPage((p) => p + 1)}
                                                    className="pagination-btn"
                                                >
                                                    Next ➡
                                                </button>
                                            </div>
                                        )}

                                        {/* Image Modal */}
                                        {selectedImage && (
                                            <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
                                                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => setSelectedImage(null)}
                                                        className="modal-close-btn"
                                                    >
                                                        ✖
                                                    </button>
                                                    <h3 className="modal-title">
                                                        {selectedImage.contentDesc || "Screenshot Preview"}
                                                    </h3>
                                                    <img
                                                        src={`${BASE_URL}${selectedImage.contentUrl}`}
                                                        alt={selectedImage.contentName}
                                                        className="modal-image"
                                                    />
                                                    <p className="modal-date">
                                                        Captured on:{" "}
                                                        {new Date(selectedImage.uploadedAt).toLocaleString("en-IN", {
                                                            hour12: true,
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Delete Confirmation Modal */}
                                        {deleteConfirm && (
                                            <div className="modal-overlay">
                                                <div className="delete-modal">
                                                    <h3 className="delete-modal-title">Confirm Deletion</h3>
                                                    <p className="delete-modal-text">
                                                        Are you sure you want to delete{" "}
                                                        <span className="filename">{deleteConfirm.contentName}</span>?
                                                    </p>
                                                    <div className="delete-modal-actions">
                                                        <button
                                                            onClick={() => setDeleteConfirm(null)}
                                                            className="btn btn-cancel"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(deleteConfirm.contentId)}
                                                            className="btn btn-delete-confirm"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <SettingsPanel />
                    </div>
                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default AdminScreenshotGallery;